import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useBooleanState } from "shared/lib/useBooleanState"
import { IconSize } from "shared/types/Icon"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  formatProjectElementParentTitlesForEntityList,
  formatProjectElementTitleForEntityList
} from "entities/attachments/ui/linked-attachments/lib/formatProjectElementParentsTitle"

import "./styles/_linked-entities-list.scss"
import { LinkedAttachmentEntity } from "./types/LinkedAttachmentEntity"

const ENTITY_ICON: Record<
  Required<LinkedAttachmentEntity>["__typename"],
  IconName
> = {
  Floor: IconName.FLOOR,
  Room: IconName.ROOM,
  ProductPosition: IconName.PRODUCTS
}

interface LinkedEntitiesListProps {
  entities: LinkedAttachmentEntity[] | null | undefined
  buttonText?: string
}

export const LinkedEntitiesList = React.memo(
  ({ entities, buttonText }: LinkedEntitiesListProps) => {
    const { t } = useTranslation()
    const { value: isExpanded, toggle } = useBooleanState(false)

    if (!entities || entities.length === 0) return null

    return (
      <div className="linked-entities-list">
        <Button
          variant={ButtonVariant.TERTIARY}
          className="linked-entities-list__toggle"
          onClick={toggle}
          aria-expanded={isExpanded}
        >
          <Icon
            name={IconName.SMALL_CARET_RIGHT}
            size={IconSize.XXS}
            className={classNames("linked-entities-list__chevron", {
              "linked-entities-list__chevron--expanded": isExpanded
            })}
          />
          <Icon name={IconName.LINK} />
          <span>{entities.length}</span>
          <span>{buttonText || t("Attachments.AttachmentLinks")}</span>
        </Button>
        {isExpanded && (
          <ul className="linked-entities-list__items">
            {entities.map(entity => {
              return entity.__typename ? (
                <li key={entity.id} className="linked-entities-list__item">
                  <Icon name={ENTITY_ICON[entity.__typename]} />
                  <span className="linked-entities-list__entity">
                    {formatProjectElementTitleForEntityList(entity)}
                  </span>
                  {formatProjectElementParentTitlesForEntityList(
                    entity.parent
                  ).map(parentTitle => {
                    return (
                      <span
                        key={parentTitle}
                        className="linked-entities-list__entity-parent"
                      >
                        {parentTitle}
                      </span>
                    )
                  })}
                </li>
              ) : null
            })}
          </ul>
        )}
      </div>
    )
  }
)
